import { useEffect, useState } from "react";
import { Link2, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function Links() {
  const [links, setLinks] = useState(null);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", subdomain: "", destination_slug: "", provider: "primary" });
  const [fd, setFd] = useState(null);
  const { canWrite } = useAuth();

  const load = () => api.get("/links").then(setLinks).catch(() => setLinks([]));
  useEffect(() => { load(); api.get("/sites").then(setSites); api.get("/freedns/status").then(setFd).catch(() => setFd({})); }, []);

  const baseFor = (provider) => provider === "freedns" ? (fd?.domain || "freedns") : "studyformath.site";

  const create = async () => {
    if (!form.name || !form.subdomain || !form.destination_slug) { toast.error("All fields required"); return; }
    try { await api.post("/links", form); toast.success("Link created"); setOpen(false); setForm({ name: "", subdomain: "", destination_slug: "", provider: "primary" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="links-page">
      <PageHeader
        title="Named Links"
        breadcrumb="cannon / links"
        subtitle="Map a subdomain to a destination site — resolved before wildcard"
        actions={canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="new-link-btn" className="inline-flex items-center gap-2 rounded-md bg-zinc-100 text-zinc-900 px-3 py-1.5 text-sm font-medium hover:bg-white"><Plus className="h-4 w-4" /> New Link</button>
            </DialogTrigger>
            <DialogContent className="bg-[#0c0c0e] border-zinc-800">
              <DialogHeader><DialogTitle className="font-head">Create Named Link</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Domain</label>
                  <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="link-provider"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-zinc-800">
                      <SelectItem value="primary">Primary · studyformath.site</SelectItem>
                      {fd?.domain && <SelectItem value="freedns">FreeDNS · {fd.domain}</SelectItem>}
                    </SelectContent>
                  </Select></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="link-name" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Subdomain</label>
                  <div className="flex items-center mt-1.5">
                    <Input value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value })} placeholder="play" className="bg-zinc-900 border-zinc-800 rounded-r-none" data-testid="link-subdomain" />
                    <span className="font-mono text-xs text-zinc-500 border border-l-0 border-zinc-800 rounded-r-md px-2 py-2 bg-zinc-900">.{baseFor(form.provider)}</span>
                  </div></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Destination Site</label>
                  <Select value={form.destination_slug} onValueChange={(v) => setForm({ ...form, destination_slug: v })}>
                    <SelectTrigger className="mt-1.5 bg-zinc-900 border-zinc-800" data-testid="link-dest"><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-zinc-800">{sites.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name} ({s.slug})</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><button onClick={create} data-testid="link-create-btn" className="rounded-md bg-zinc-100 text-zinc-900 px-4 py-2 text-sm font-medium hover:bg-white">Create</button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {!links ? <Loading /> : links.length === 0 ? <EmptyState icon={Link2} title="No named links" /> : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-800 bg-zinc-900/50 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Route</th><th className="px-4 py-2.5 w-10"></th></tr></thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.link_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`link-row-${l.link_id}`}>
                  <td className="px-4 py-3 text-zinc-200">{l.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400 flex items-center gap-2">
                    <span className="text-sky-400">{l.subdomain}.{l.base_domain || "studyformath.site"}</span> <ArrowRight className="h-3 w-3 text-zinc-600" /> <span className="text-violet-400">{l.destination_slug}</span>
                    {l.provider === "freedns" && <span className="ml-1 text-[10px] uppercase font-mono text-emerald-400 border border-emerald-500/20 rounded px-1">freedns</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && <button onClick={async () => { await api.del(`/links/${l.link_id}`); load(); }} className="p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
